/**
 * Internal pageview tracking via the Holdbar tracking API.
 *
 * Ported from legacy `apps/web/src/utils/tracking/plugins/holdbar-custom-plugin.ts`.
 * On each pageview, POSTs event data to the internal beacon endpoint which
 * forwards it to the tracking API as query parameters.
 */

import { href } from 'react-router';

export type PageviewConfig = {
  path: string;
  locale?: string;
  companyId?: string;
};

/**
 * Sends a pageview event to the internal tracking API.
 *
 * The event is sent via `fetch()` with `keepalive: true` to ensure
 * delivery even during navigation.
 */
export function trackPageview(config: PageviewConfig): void {
  try {
    const { path, locale, companyId } = config;

    const modelId = getModelId(path);
    const modelType = getModelType(path);
    const pageType = getPageType(path);

    const event: Record<string, string | number> = {
      t: 'pageview',
      r: document.referrer,
      q: window.location.search,
      p: window.location.pathname,
      url: window.location.href,
      sw: window.innerWidth,
      sh: window.innerHeight,
      pt: document.title,
      h: window.location.hash,
      ...(locale && { l: locale }),
      ...(pageType && { pty: pageType }),
      ...(companyId && { c: companyId }),
      ...(modelId && { mid: modelId }),
      ...(modelType && { mt: modelType }),
    };

    // POST to the resource route which proxies to the tracking API
    navigator.sendBeacon(href('/api/v2/beacon'), JSON.stringify(event));
  } catch {
    // Defensive — never let tracking errors propagate
  }
}

function getModelId(path: string): string | undefined {
  const [, , id] = path.split('/');
  if (getModelType(path) && id?.length === 32) {
    return id;
  }
  return undefined;
}

function getModelType(path: string): string | undefined {
  const [, type] = path.split('/');
  switch (type) {
    case 'checkout':
      return 'experience';
    case 'experience':
    case 'receipt':
    case 'voucher':
      return type;
    default:
      return undefined;
  }
}

function getPageType(path: string): string | undefined {
  const [, type] = path.split('/');
  switch (type) {
    case 'checkout':
      return 'experienceCheckout';
    case 'vouchers':
      return 'voucherCheckout';
    case 'experience':
    case 'receipt':
    case 'voucher':
      return type;
    default:
      return 'companyProfile';
  }
}
